import {Button, Drawer, Rate, Result, Space} from "antd";
import {CloseOutlined, EditOutlined} from "@ant-design/icons";
import {CustomerServiceOrderDrawerOrderInfo} from ".";
import moment from "moment";
import {mapStatusIdToStatus} from "../../../list/list";
import {useGetCustomerStoreServiceOrderApi} from "../../../../../../../react-query-hooks/store/services/get-store-service-order";
import {FullPageLoader} from "../../../../../../full-page-loader";
import {SetSelectedCustomerStoreServiceOrderInfoPayload} from "../../../../../../../service-feature/useCases/get-orders/reducers/customer";
import {useGetCustomerReview} from "../../../../../../../react-query-hooks/reviews/get-reviews";
import {useGetCustomerStoreServicesOrdersContext} from "../../../../../../../service-feature/useCases/get-orders/context/customer";
import {
  StyledCustomerReviewBlock,
  StyledCustomerReviewBlockStarsAndDate,
  StyledCustomerReviewBlockStarsAndDateWrapper,
} from "../../../../../../calls/past-calls/past-call-card/customer/styles";
import {StyledStoreServiceOrderDrawer} from "../styles";

type CustomerStoreServiceOrderDrawerProps = {
  orderDetails: SetSelectedCustomerStoreServiceOrderInfoPayload;
  openOrderInfoDrawer: boolean;
  onOrderInfoDrawerClose: () => void;
};

// Delivered on July 16, 2025

export function CustomerStoreServiceOrderDrawer({
  orderDetails,
  onOrderInfoDrawerClose,
  openOrderInfoDrawer,
}: CustomerStoreServiceOrderDrawerProps) {
  const {dispatch} = useGetCustomerStoreServicesOrdersContext();
  const getStoreServiceOrderQuery = useGetCustomerStoreServiceOrderApi(
    orderDetails.order.id
  );
  const reviewOfferingId = orderDetails.productService.id;
  const customerEmail = orderDetails.order.emailAddress;
  const getCustomerReview = useGetCustomerReview({
    emailAddress: customerEmail,
    offering_id: reviewOfferingId,
    offering_type: "product-service",
  });

  // `useGetCustomerReview` is disabled without both an offering id and an email,
  // so without them we can neither tell whether a review already exists nor file
  // a valid one. v2 supplies both now — this only guards malformed data.
  const canLeaveReview = reviewOfferingId > 0 && !!customerEmail;
  const review = getCustomerReview.data;

  const status = mapStatusIdToStatus(orderDetails.order.statusId);
  const coachName =
    orderDetails.order.coachDisplayName?.trim() ||
    `${orderDetails.order.firstName} ${orderDetails.order.lastName}`.trim();

  return (
    <StyledStoreServiceOrderDrawer
      title={orderDetails.productService.name}
      placement="right"
      onClose={onOrderInfoDrawerClose}
      visible={openOrderInfoDrawer}
      closable={false}
      extra={
        <Button size="small" type="text" onClick={onOrderInfoDrawerClose}>
          <CloseOutlined />
        </Button>
      }
      footer={
        !canLeaveReview ||
        getCustomerReview.isSuccess ||
        status !== "completed" ? null : (
          <>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                dispatch({
                  type: "set-active-store-service-order-drawer",
                  payload: "leave-a-review",
                });
              }}
            >
              Leave a review
            </Button>
          </>
        )
      }
    >
      {getStoreServiceOrderQuery.isLoading ? (
        <FullPageLoader />
      ) : getStoreServiceOrderQuery.isError ? (
        <Result
          status="500"
          title="Sorry, something went wrong."
          subTitle="Error fetching order"
          extra={
            <Button
              type="primary"
              size="small"
              onClick={() => getStoreServiceOrderQuery.refetch()}
            >
              Try again
            </Button>
          }
        />
      ) : (
        <Space direction="vertical" style={{display: "flex"}} size={16}>
          {/* Order-level fields come from the selected list item (orderDetails);
              the detail query supplies only the work-delivery submissions. */}
          <CustomerServiceOrderDrawerOrderInfo
            orderTime={`Ordered ${moment(orderDetails.order.createdAt).fromNow()}`}
            status={status}
            coachName={coachName}
            deliveryInfo={moment(orderDetails.order.delivery_date).format(
              "MMMM DD, YYYY"
            )}
            deliveryDate={orderDetails.order.delivery_date}
            price={
              +orderDetails.order.amount > 0
                ? `${orderDetails.order.currency} ${orderDetails.order.amount}`
                : "Free"
            }
            productServiceDeliveries={
              getStoreServiceOrderQuery.data?.productServiceDelivery ?? []
            }
          />
          <div>
            <Space direction="vertical" style={{display: "flex"}} size={16}>
              {/* `isInitialLoading`, not `isLoading`: a disabled query reports
                  `isLoading` true forever, which would stick on "Loading review".
                  The block itself is keyed off the fetched review, so an errored
                  or never-run lookup renders nothing instead of crashing. */}
              {getCustomerReview.isInitialLoading ? (
                <p>Loading review</p>
              ) : !review ? null : (
                <>
                  <p
                    style={{
                      fontWeight: 600,
                      fontSize: "1.6rem",
                    }}
                  >
                    Your review
                  </p>
                  <StyledCustomerReviewBlock>
                    <Space direction="vertical" style={{display: "flex"}} size={16}>
                      <StyledCustomerReviewBlockStarsAndDateWrapper>
                        <StyledCustomerReviewBlockStarsAndDate>
                          <Rate
                            disabled
                            defaultValue={review.rating}
                            value={review.rating}
                            style={{color: "#99C78F"}}
                            allowHalf
                          />
                          <p>{new Date(review.updatedAt).toDateString()}</p>
                        </StyledCustomerReviewBlockStarsAndDate>
                        {/* {getCustomerCanEditReview.data?.canEdit && ( */}
                        <Button
                          onClick={() => {
                            dispatch({
                              type: "set-active-store-service-order-drawer",
                              payload: "leave-a-review",
                            });
                          }}
                          size="small"
                          type="text"
                          icon={<EditOutlined style={{fontSize: "1.6rem"}} />}
                        />
                        {/* )} */}
                      </StyledCustomerReviewBlockStarsAndDateWrapper>
                      <p>{review.review}</p>
                    </Space>
                  </StyledCustomerReviewBlock>
                </>
              )}
            </Space>
          </div>
        </Space>
      )}
    </StyledStoreServiceOrderDrawer>
  );
}
